import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { z } from "zod";

import { HbolStatusBadge } from "@/components/hbol/status-badge";
import { NewImportDialog } from "@/components/hbol/new-import-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { hbolJobsQuery, type HbolJobListItem } from "@/lib/hbol";

const searchSchema = z.object({
  search: z.string().optional(),
  page: z.number().int().min(1).optional(),
});

export const Route = createFileRoute("/_authenticated/hbol/")({
  validateSearch: searchSchema,
  component: HbolImportsPage,
});

function HbolImportsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(search.search ?? "");
  const list = useQuery(hbolJobsQuery({ search: search.search, page: search.page }));

  const rows = list.data?.results ?? [];
  const count = list.data?.count ?? 0;
  const page = search.page ?? 1;
  const lastPage = Math.max(1, Math.ceil(count / 50));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div>
          <h1 className="text-base font-semibold">HBOL import</h1>
          <p className="text-muted-foreground text-xs">
            A forwarder&apos;s pre-alert in, the house-bill import template for Master Cargo&apos;s
            system out.
          </p>
        </div>
        <Button size="sm" className="ml-auto" onClick={() => setCreating(true)}>
          New import
        </Button>
      </div>

      <section className="bg-background rounded-md border">
        <header className="flex flex-wrap items-center gap-3 border-b px-3 py-2">
          <h2 className="text-xs font-semibold tracking-wide uppercase">Imports</h2>
          <form
            className="ml-auto flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void navigate({ search: { search: draft || undefined } });
            }}
          >
            <Input
              placeholder="Search name, house B/L, consignee…"
              className="h-8 w-72"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button type="submit" size="sm" variant="outline">
              Search
            </Button>
            {search.search && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft("");
                  void navigate({ search: {} });
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </header>
        <Table>
          <TableHeader>
            <TableRow className="text-muted-foreground text-[10px] uppercase">
              <TableHead className="h-8 font-medium">Import</TableHead>
              <TableHead className="h-8 font-medium">Forwarder</TableHead>
              <TableHead className="h-8 font-medium">Status</TableHead>
              <TableHead className="h-8 text-right font-medium">Files</TableHead>
              <TableHead className="h-8 text-right font-medium">House bills</TableHead>
              <TableHead className="h-8 font-medium">Created</TableHead>
              <TableHead className="h-8 font-medium">By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground py-10 text-center">
                  {list.isLoading ? (
                    "Loading…"
                  ) : search.search ? (
                    "No imports match."
                  ) : (
                    <span>
                      No imports yet.{" "}
                      <button className="text-primary underline" onClick={() => setCreating(true)}>
                        Upload the first pre-alert
                      </button>
                      .
                    </span>
                  )}
                </TableCell>
              </TableRow>
            )}
            {rows.map((job) => (
              <JobRow key={job.id} job={job} />
            ))}
          </TableBody>
        </Table>
      </section>

      {lastPage > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => navigate({ search: { ...search, page: page - 1 } })}
          >
            Previous
          </Button>
          <span>
            Page {page} of {lastPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => navigate({ search: { ...search, page: page + 1 } })}
          >
            Next
          </Button>
        </div>
      )}

      <NewImportDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function JobRow({ job }: { job: HbolJobListItem }) {
  const navigate = useNavigate();
  const open = () => void navigate({ to: "/hbol/$jobId", params: { jobId: job.id } });
  return (
    <TableRow
      className="cursor-pointer"
      onClick={open}
      onKeyDown={(e) => e.key === "Enter" && open()}
      tabIndex={0}
    >
      <TableCell>
        <div className="font-mono text-[13px]">{job.display_title}</div>
        {job.title && job.container && job.title !== job.container && (
          <div className="text-muted-foreground font-mono text-xs">{job.container}</div>
        )}
      </TableCell>
      <TableCell>{job.forwarder || "—"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {job.status === "reading" && (
            <Loader2 className="text-muted-foreground size-3.5 animate-spin" />
          )}
          <HbolStatusBadge status={job.status} />
        </div>
      </TableCell>
      <TableCell className="text-right font-mono text-[13px]">{job.file_count}</TableCell>
      <TableCell className="text-right font-mono text-[13px]">{job.row_count}</TableCell>
      <TableCell title={new Date(job.created_at).toLocaleString()}>
        {new Date(job.created_at).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-muted-foreground">{job.created_by_email}</TableCell>
    </TableRow>
  );
}
